import React, { createContext, useState, useEffect } from 'react';
import authService from '../api/auth';
import { useUniversity } from './UniversityContext';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const { changeUniversity } = useUniversity();
  
  // Verificar el token al inicio
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Primero intentar restaurar desde localStorage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('authToken');
        
        if (!token) {
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        // Establecer estado inicial con datos del localStorage
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
          } catch (e) {
            console.error("Error al parsear usuario almacenado:", e);
            localStorage.removeItem('user');
          }
        }
        
        // Validar el token con el servidor
        const { success, user: validatedUser } = await authService.validateToken();
        
        if (success && validatedUser) {
          setUser(validatedUser);
          setIsAuthenticated(true);
          // Actualizar localStorage con info del servidor
          localStorage.setItem('user', JSON.stringify(validatedUser));
        } else {
          console.warn("Token inválido según el servidor");
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error('Error al validar token:', error);
        
        // Si es un error de red, mantener la sesión local
        if (!error.response) {
          console.warn("Error de red, manteniendo sesión local");
        } 
        // Solo limpiar si es un error de autenticación explícito
        else if (error.response && error.response.status === 401) {
          console.warn("Error 401, limpiando sesión");
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);
  
  const login = async (credentials) => {
    setLoading(true);
    
    try {
      // Limpiar credenciales antes de enviar
      const cleanCredentials = {
        ...credentials,
        username: credentials.username?.trim(),
        email: credentials.email?.trim(),
        password: credentials.password?.trim()
      };

      const response = await authService.login(cleanCredentials);
      
      if (response.success && response.token && response.user) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        // Actualizar estado del contexto
        setUser(response.user);
        setIsAuthenticated(true);
        
        // Actualizar la universidad seleccionada con la del usuario
        if (response.user.university) {
          localStorage.setItem('selectedUniversity', response.user.university);
          changeUniversity(response.user.university);
        }
        
        return { 
          success: true,
          user: response.user 
        };
      } else {
        return { 
          success: false, 
          message: response.message || 'Error al iniciar sesión' 
        };
      }
    } catch (error) {
      console.error('Error durante login:', error);
      return { 
        success: false, 
        message: error.message || 'Error de conexión al servidor' 
      };
    } finally {
      setLoading(false);
    }
  };
  
  const register = async (username, email, password, university) => {
    try {
      // Limpiar datos antes de enviar
      const cleanUsername = username.trim();
      const cleanEmail = email.trim();
      const cleanPassword = password.trim();

      const response = await authService.register(cleanUsername, cleanEmail, cleanPassword, university);
      if (response.success) {
        // Solo establecer usuario si no requiere verificación
        if (!response.requiresVerification) {
          setUser(response.user);
          setIsAuthenticated(true);
        }
        return response;
      } else {
        return { success: false, message: response.message || 'Error al registrarse' };
      }
    } catch (error) {
      console.error('Error en registro:', error);
      return { success: false, message: error.message || 'Error al registrarse' };
    }
  };
  
  const verifyEmail = async (token) => {
    try {
      const response = await authService.verifyEmail(token);
      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: response.message || 'Error en la verificación' };
      }
    } catch (error) {
      console.error('Error en verificación de correo:', error);
      return { success: false, message: error.message || 'Error en la verificación' };
    }
  };
  
  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };
  
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };
  
  // Agregar esta función para verificar si el token ha expirado
  const isTokenExpired = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return true;
    
    try {
      // Verificar si el token tiene el formato correcto de JWT
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn("El token no tiene formato JWT válido");
        return true;
      }
      
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const { exp } = JSON.parse(jsonPayload);
      
      // Verificar si exp existe
      if (!exp) {
        console.warn("El token no contiene información de expiración");
        return false; // Asumir que no ha expirado si no hay exp
      }
      
      const expired = Date.now() >= exp * 1000;
      
      if (expired) {
        // Limpiar datos de sesión si el token ha expirado
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        setUser(null);
        setIsAuthenticated(false);
      }
      
      return expired;
    } catch (error) {
      console.error('Error verificando expiración del token:', error);
      // No limpiar automáticamente en caso de error de parsing
      return false; // Considerar no expirado en caso de error para evitar desconexiones innecesarias
    }
  };
  
  // Agregar esta función para refrescar el token
  const refreshToken = async () => {
    try {
      // Si el token no ha expirado, no necesitamos refrescarlo
      if (!isTokenExpired()) {
        return true;
      }
      
      // Intentar validar/refrescar el token con el servidor
      const { success, user: refreshedUser } = await authService.validateToken();
      
      if (success && refreshedUser) {
        setUser(refreshedUser);
        setIsAuthenticated(true);
        return true;
      } else {
        // Si falla, limpiar la sesión
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        return false;
      }
    } catch (error) {
      console.error('Error al refrescar token:', error);
      return false;
    }
  };
  
  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated, 
        loading,
        login,
        register,
        verifyEmail,
        logout,
        updateUser,
        isTokenExpired,
        refreshToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
